// Centralized API client for RingEnterpriseBridgeService UI.
//
// Design choice: native fetch. Reasoning:
//   1. POC — minimize bundle and dep footprint.
//   2. The wrapper is small enough that axios interceptor ergonomics
//      aren't a material win.
//   3. Native fetch is the modern baseline; eventual migration to a
//      react-query / swr layer composes on top without ripping out axios.
//
// Invariants enforced here (do NOT relax without updating DESIGN.md §9
// and the eslint rule in .eslintrc.cjs):
//
//   - Every /api/* request carries X-Acting-Role from sessionStorage.
//     NEVER localStorage (DESIGN.md §9 "per-tab isolation is load-bearing"
//     and LLD §4.7).
//   - On 401, the wrapper redirects to /signin?reason=<code>. Calling
//     components do not have to handle auth expiry.
//   - The wrapper surfaces X-Request-ID to callers (DESIGN.md §7.6) so
//     toasts carry the operator's diagnostic handle.
//   - Components in src/components/** and src/pages/** must import from
//     "@/api" only. Direct fetch() calls outside src/api/ are banned by
//     eslint (no-restricted-syntax).

const CURRENT_ROLE_KEY = "currentRole";
const ACTING_ROLE_HEADER = "X-Acting-Role";
const REQUEST_ID_HEADER = "X-Request-ID";

/**
 * Error thrown by the api client on non-2xx responses. Callers get the
 * parsed backend error body AND the X-Request-ID so they can feed both
 * to toasts.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly requestId: string | undefined;
  readonly body: unknown;

  constructor(
    status: number,
    code: string | undefined,
    message: string,
    requestId: string | undefined,
    body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.body = body;
  }
}

/**
 * Structured result for callers that need to surface the request id alongside
 * the parsed body (e.g. toasts, DebugPanel). Most callers will use `api.get` /
 * `api.post` which unwrap `.data` after error handling; this shape is available
 * for the few callers that truly need both.
 */
export interface ApiResponse<T> {
  data: T;
  requestId: string | undefined;
  status: number;
}

export interface ApiRequestInit extends Omit<RequestInit, "body"> {
  /** Arbitrary JSON-serializable body. Passing a string/Blob/FormData is also allowed. */
  body?: unknown;
  /**
   * Skip the X-Acting-Role header. Only used for the short list of
   * backend endpoints that do NOT require role (LLD §6.1, §4.7):
   *   - GET  /api/session/me
   *   - POST /api/session/login
   *   - POST /api/session/logout
   *   - GET  /api/health, /api/ready
   * Default: false (header is injected).
   */
  skipActingRole?: boolean;
  /** Skip the automatic 401 -> /signin redirect. Used by SessionProvider's bootstrap fetch. */
  skipAuthRedirect?: boolean;
}

/**
 * Read the currently-selected role from sessionStorage.
 *
 * Returns `null` (not undefined) when unset so callers can explicitly branch
 * on "no role picked yet". ssr/jsdom safe.
 */
export function getCurrentRole(): string | null {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  return window.sessionStorage.getItem(CURRENT_ROLE_KEY);
}

export function setCurrentRole(role: string): void {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  window.sessionStorage.setItem(CURRENT_ROLE_KEY, role);
}

export function clearCurrentRole(): void {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  window.sessionStorage.removeItem(CURRENT_ROLE_KEY);
}

/**
 * Hook that lets callers plug in a role-picker trigger. The fetch wrapper
 * calls this when `X-Acting-Role` is required but unset; the UI then shows
 * the picker modal (implemented in task 42).
 *
 * Until task 42 lands, the default impl redirects to /signin with a reason
 * so the operator is never silently blackholed without a header.
 */
let missingRoleHandler: (path: string) => void = (path) => {
  // TODO(task 42.2): replace with role-picker modal trigger.
  // Until then: bail to /signin with a diagnostic reason so we never
  // silently ship a request with no role header.
  if (typeof window !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      `[api] X-Acting-Role required for ${path} but sessionStorage.currentRole is unset. ` +
        `Redirecting to /signin. (Replace with role-picker trigger in task 42.)`,
    );
    window.location.assign("/signin?reason=role_required");
  }
};

export function setMissingRoleHandler(handler: (path: string) => void): void {
  missingRoleHandler = handler;
}

function isApiPath(url: string): boolean {
  return url.startsWith("/api/") || url === "/api";
}

function buildHeaders(
  path: string,
  init: ApiRequestInit | undefined,
): { headers: Headers; aborted: boolean } {
  const headers = new Headers(init?.headers ?? {});

  // Force JSON by default; callers can override via init.headers.
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  if (init?.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const requiresActingRole =
    isApiPath(path) && init?.skipActingRole !== true;
  if (requiresActingRole) {
    const role = getCurrentRole();
    if (role === null || role === "") {
      // Hand off to the role-picker flow and abort the request.
      missingRoleHandler(path);
      return { headers, aborted: true };
    }
    headers.set(ACTING_ROLE_HEADER, role);
  }

  return { headers, aborted: false };
}

function serializeBody(body: unknown): BodyInit | undefined {
  if (body === undefined || body === null) return undefined;
  if (typeof body === "string") return body;
  if (body instanceof FormData) return body;
  if (body instanceof Blob) return body;
  if (body instanceof URLSearchParams) return body;
  if (body instanceof ArrayBuffer) return body;
  return JSON.stringify(body);
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("Content-Type") ?? "";
  if (contentType.includes("application/json") || contentType.includes("+json")) {
    // Empty 204 responses will throw on .json(); guard that.
    const text = await response.text();
    if (text.length === 0) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  if (response.status === 204) return null;
  return response.text();
}

function handleUnauthorized(response: Response, init?: ApiRequestInit): void {
  if (init?.skipAuthRedirect) return;
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/signin") return;
  // Clear any stored role — sessionStorage.currentRole is scoped to a
  // logged-in session; if we're kicking to /signin, the next session will
  // re-resolve via 42.4 → 42.1 → 42.2 and must not inherit a stale pick.
  // Idempotent with SessionProvider's own clear on its 401 handler.
  clearCurrentRole();
  const body = { reason: response.headers.get("X-Error-Code") ?? "session_expired" };
  const qs = new URLSearchParams({ reason: body.reason }).toString();
  window.location.assign(`/signin?${qs}`);
}

/**
 * Core request function. Returns `null` from an aborted-for-missing-role
 * call so callers can cleanly exit without a thrown error (the redirect /
 * modal is already in flight).
 */
export async function request<T>(
  path: string,
  init?: ApiRequestInit,
): Promise<ApiResponse<T> | null> {
  const { headers, aborted } = buildHeaders(path, init);
  if (aborted) return null;

  const response = await fetch(path, {
    ...init,
    headers,
    body: serializeBody(init?.body),
    credentials: init?.credentials ?? "same-origin",
  });

  const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;

  if (response.status === 401) {
    handleUnauthorized(response, init);
    // Still throw — callers that opted out of the redirect need the error;
    // callers that didn't will redirect before they can read it.
    throw new ApiError(401, "unauthorized", "Unauthorized", requestId, null);
  }

  const body = await parseBody(response);

  if (!response.ok) {
    // Error code precedence: the legacy AbstractHandler endpoints emit
    // {"error":"<code>"}, while the Smithy @Operation endpoints emit Coral's
    // standard modeled-error body {"message":"<code>"}. Accept either so the
    // code survives the transport migration without per-call-site changes.
    const obj =
      typeof body === "object" && body !== null
        ? (body as { error?: unknown; message?: unknown })
        : undefined;
    const code =
      obj && "error" in obj && obj.error != null
        ? String(obj.error)
        : obj && "message" in obj && obj.message != null
          ? String(obj.message)
          : String(response.status);
    const message =
      obj && "message" in obj && obj.message != null
        ? String(obj.message)
        : response.statusText;
    throw new ApiError(response.status, code, message, requestId, body);
  }

  return { data: body as T, requestId, status: response.status };
}

/** Typed convenience helpers. All go through `request`. */
export const api = {
  async get<T>(path: string, init?: ApiRequestInit): Promise<T> {
    const res = await request<T>(path, { ...init, method: "GET" });
    if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
    return res.data;
  },
  async post<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
    const res = await request<T>(path, { ...init, method: "POST", body });
    if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
    return res.data;
  },
  async patch<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
    const res = await request<T>(path, { ...init, method: "PATCH", body });
    if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
    return res.data;
  },
  async delete<T>(path: string, init?: ApiRequestInit): Promise<T> {
    const res = await request<T>(path, { ...init, method: "DELETE" });
    if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
    return res.data;
  },
  /** For callers that need request-id access (toasts, DebugPanel). */
  raw: request,
};
